import {Combo, PopupAnchor, SourceAnchor} from '@mintdata-wsp/common/components/combo/Combo';
import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {Snippet, SnippetPosition} from '@mintdata-wsp/common/state/DataState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {DataLineColorType} from '@mintdata-wsp/wsp/ui/components/dataline/DataLine';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {ErrorPopupSide, ErrorStyle, Input, InputSize, InputStyle} from '@mintdata-wsp/wsp/ui/components/forms/Input';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {ButtonSize, ButtonStyle, WSPButton} from '@mintdata-wsp/wsp/ui/components/general/WSPButton';
import {SnippetPositionTitle} from '@mintdata-wsp/wsp/ui/views/workspace/settings/organization/snippets/SnippetCard';
import {IListItemState, ListModel} from '@mintdata/mint-kit/api/models/List';
import {PopupSize} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {Checkbox, CheckboxStyle} from '@mintdata/mint-kit/impl/properties/components/checkbox/Checkbox';
import {IStyles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./EditSnippet.less');
const cx = classnames.bind(styles);

export interface CreateSnippetProps
{
    snippet?:Snippet;
    onClose:() => void;
    onChange:(snippet:Snippet) => void;
    className?:string;
    buttonTitle:string;
}

@observer
export class EditSnippet extends React.Component<CreateSnippetProps, any>
{
    @observable
    private _snippet:Snippet;

    @observable
    private _viewNewSnippet:boolean = false;

    private _dropdownPositionItems;

    componentWillMount():void
    {
        const {snippet} = this.props;

        this._initSnippet(snippet);
        this._initPositionsItems(snippet);
    }

    render()
    {
        const {className, buttonTitle} = this.props;

        const snippet:Snippet = this._snippet;

        const isDisabled = !this._isValidInputs();

        const colorType = snippet.enabled ? DataLineColorType.Blue : DataLineColorType.Gray;
        const colorTypeStr = 'colorType-' + DataLineColorType[colorType];

        return (
            <Row className={cx('createSnippet', className, colorTypeStr)}>

                <Column className={cx('fields')}>

                    <Row className={cx('firstLine')}>
                        <Input
                            className={cx('inputNameWrapper')}
                            inputClassName={cx('inputName')}
                            style={InputStyle.Simple}
                            size={InputSize.Small}
                            placeholder={'Snippet name'}
                            initialValue={snippet.name}
                            isInitiallyFocused={true}
                            isInitiallySelected={true}
                            onChange={this._onChangeName}
                            onEnterKey={this._buttonCreateSnippet}

                            isValidFn={(name:string) =>
                            {
                                return this._isValidName(name, snippet.id);
                            }}
                            getErrorMessageFn={() => 'Snippet name should be unique'}
                            errorStyle={ErrorStyle.BdpForms}
                            errorPopupSide={ErrorPopupSide.Bottom}
                        />

                        <Combo
                            className={cx('dropdownPosition')}
                            dataSource={this._dropdownPositionItems}
                            onSelectItem={this._onSelectPosition}
                            contentRenderer={this._positionCaptionRenderer}
                            itemRenderer={this._positionItemsRenderer}
                            sourceAnchor={SourceAnchor.RightBottom}
                            popoverAnchor={PopupAnchor.RightTop}
                            popupSize={PopupSize.Original}
                            theme={{content: cx('dropdownPosition-items')}}
                        />

                        <Checkbox
                            style={CheckboxStyle.Toggler}
                            isChecked={snippet.enabled}
                            onClick={this._onClickCheckbox}
                        />
                    </Row>

                    <Input
                        multiline
                        className={cx('inputTextWrapper')}
                        inputClassName={cx('inputText')}
                        style={InputStyle.Simple}
                        size={InputSize.Small}
                        placeholder={'Snippet Text'}
                        initialValue={snippet.embedText}
                        onChange={this._onChangeText}
                        // onEnterKey={this._buttonCreateSnippet}
                    />

                    <Row className={cx('btnCreateWrapper')}>
                        <WSPButton
                            className={cx('btnCreate')}
                            caption={buttonTitle}
                            style={ButtonStyle.Simple}
                            size={ButtonSize.Small}
                            onClick={this._buttonCreateSnippet}
                            isDisabled={isDisabled}
                        />
                    </Row>
                </Column>

                <WSPButton
                    className={cx('btnClose')}
                    icon={MDIcons.common.Close}
                    style={ButtonStyle.SimpleSecondary}
                    onClick={this._buttonCloseCreateSnippet}
                />

            </Row>
        );
    }

    @action.bound
    private _initSnippet(snippet:Snippet)
    {
        if( snippet )
            this._snippet = {...snippet};
        else
            this._snippet = Snippet.empty();
    }

    @action.bound
    _onChangeName(newValue:string)
    {
        this._snippet.name = newValue;
    }

    @action.bound
    _onChangeText(newValue:string)
    {
        this._snippet.embedText = newValue;
    }

    @action.bound
    private _onClickCheckbox(event:React.MouseEvent<HTMLElement>)
    {
        this._snippet.enabled = !this._snippet.enabled;
    }

    @bind
    _buttonCreateSnippet()
    {
        const {onChange} = this.props;

        onChange(this._snippet);
    }

    @bind
    _buttonCloseCreateSnippet()
    {
        const {onClose} = this.props;

        onClose();
    }

    @bind
    private _isValidName(newName:string, snippetId:number)
    {
        // if( !this._snippet ) return false;
        if( !newName ) return false;

        // const name = this._snippet.name.trim();
        const name = newName.trim();

        return name !== '' && controllers.data.snippets.isValidName(name, snippetId);
    }

    @bind
    private _isValidInputs()
    {
        const {id, name, embedText} = this._snippet;

        return this._isValidName(name, id) && embedText.trim() !== '';
    }

    @action.bound
    private _initPositionsItems(snippet:Snippet)
    {
        const embedPosition = snippet ? snippet.embedPosition : SnippetPosition.BODY_END;

        const positionItems = [
            {
                position: SnippetPosition.HEAD_START,
                title: SnippetPositionTitle[SnippetPosition.HEAD_START]
            },
            {
                position: SnippetPosition.HEAD_END,
                title: SnippetPositionTitle[SnippetPosition.HEAD_END]
            },
            {
                position: SnippetPosition.BODY_START,
                title: SnippetPositionTitle[SnippetPosition.BODY_START]
            },
            {
                position: SnippetPosition.BODY_END,
                title: SnippetPositionTitle[SnippetPosition.BODY_END]
            },
        ];
        this._dropdownPositionItems = new ListModel(positionItems);
        this._dropdownPositionItems.selectByValueProperty(embedPosition, 'position');
    }

    @action.bound
    private _onSelectPosition(positionItem)
    {
        this._snippet.embedPosition = positionItem.position;
    }

    @bind
    _positionCaptionRenderer(data:any, state:any, styles:IStyles):JSX.Element
    {
        const {title} = data;
        const {isOpened} = state;

        return (
            <div className={cx('dropdownPosition-caption', {isOpened})}>

                <span className={cx('dropdownPosition-title')}>
                    {title}
                </span>

                <MDIcon
                    className={cx('dropdownPosition-icon')}
                    name={MDIcons.common.ArrowDropdown}
                    size={MDIconSize.medium16}
                />
            </div>
        );
    }

    @bind
    _positionItemsRenderer(data:any, state:IListItemState, styles:IStyles):JSX.Element
    {
        const {title} = data;
        const {isSelected} = state;

        return (
            <Row className={cx('dropdownPosition-item', {isSelected})}>

                <MDIcon
                    className={cx('dropdownPosition-icon')}
                    name={MDIcons.common.Checkmark}
                />
                <div>
                    {title}
                </div>
            </Row>
        );
    }
}
